import { brandAs, type OptionId, type QuestionId, type Seed } from '../model/brands.ts';
import type { CanonicalExam, CanonicalQuestion } from '../model/canonical.ts';
import { incorrectCount, NO_HISTORY, type ExamHistory } from '../model/history.ts';
import { prngFrom } from '../randomization/prng.ts';
import { shuffle, shuffleAnchored } from '../randomization/shuffle.ts';
import type { SelectionCriteria, SessionPolicy } from './policy.ts';

/** Pinned in every exported session, so a future version can still reproduce an old one. */
export const SHUFFLE_ALGORITHM = 'freexam-shuffle-1';

export interface SessionBlueprint {
  readonly algorithm: typeof SHUFFLE_ALGORITHM;
  readonly seed: Seed;
  readonly questionOrder: readonly QuestionId[];
  /** Only for questions whose options were shuffled; the rest are shown as authored. */
  readonly optionOrder: Readonly<Record<string, readonly OptionId[]>>;
}

export interface BlueprintResult {
  readonly blueprint: SessionBlueprint;
  /** How many questions matched the filters, before `count` was applied. */
  readonly matched: number;
  /** True when fewer questions matched than were asked for. */
  readonly reduced: boolean;
}

/**
 * Builds the plan for one session: which questions, in which order, with options in which order.
 *
 * Pure and deterministic. The same exam hash, criteria, policy and seed produce the same blueprint
 * on any machine — which is what makes "share this seed" a real feature and what lets a session
 * recorded today be replayed later.
 *
 * The ordering of the steps is the reproducibility contract:
 *
 *  1. **Sort by id first.** The starting order must not be the order the file happened to list, or
 *     re-serialising an exam would silently change every session drawn from it.
 *  2. **Filter.** Pure predicates, so the order they are applied in cannot matter.
 *  3. **Shuffle** with a generator seeded from the exam hash, the criteria and the seed.
 *  4. **Take `count`.**
 *  5. **Shuffle each question's options with its own generator**, seeded from the exam hash, the
 *     seed and the question id — and deliberately *not* from the filters or the count. This is the
 *     step that matters: shortening a set or narrowing a filter leaves the questions that stayed
 *     looking exactly as they did, which is what makes repeated practice feel stable instead of
 *     scrambled.
 */
export function buildBlueprint(
  exam: CanonicalExam,
  criteria: SelectionCriteria,
  policy: SessionPolicy,
  seed: Seed,
  history: ExamHistory = NO_HISTORY,
): BlueprintResult {
  const selection = selectionKey(exam, criteria, seed);
  // Deliberately NOT the selection key: option order must not depend on the filters or on how many
  // questions were drawn. See the note on optionKey.
  const options = optionKey(exam, seed);

  const sorted = [...exam.questions].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const matching = sorted.filter((question) =>
    matches(question, criteria, sourceNumber(exam, question.id), history),
  );

  const ordered = policy.shuffleQuestions ? shuffle(matching, prngFrom(selection)) : matching;

  const requested = criteria.count === 'all' ? matching.length : criteria.count;
  const taken = ordered.slice(0, Math.max(0, Math.min(requested, ordered.length)));

  const optionOrder: Record<string, readonly OptionId[]> = {};
  for (const question of taken) {
    const order = shuffledOptionIds(question, policy, options);
    if (order !== null) {
      optionOrder[question.id] = order;
    }
  }

  return {
    blueprint: {
      algorithm: SHUFFLE_ALGORITHM,
      seed,
      questionOrder: taken.map((question) => question.id),
      optionOrder,
    },
    matched: matching.length,
    reduced: criteria.count !== 'all' && matching.length < requested,
  };
}

/** How many questions the current filters would draw from, without building a blueprint. */
export function countMatching(
  exam: CanonicalExam,
  criteria: SelectionCriteria,
  history: ExamHistory = NO_HISTORY,
): number {
  return exam.questions.filter((question) =>
    matches(question, criteria, sourceNumber(exam, question.id), history),
  ).length;
}

/**
 * The number this question carries in the file, counting from 1.
 *
 * The one number a candidate can use to talk to whoever wrote the exam — "question 190 is wrong" —
 * so it follows the file, not the session. It survives filtering and shuffling untouched.
 */
export function sourceNumber(exam: CanonicalExam, questionId: QuestionId): number {
  const position = exam.index.byId[questionId];
  return position === undefined ? 0 : position + 1;
}

function matches(
  question: CanonicalQuestion,
  criteria: SelectionCriteria,
  position: number,
  history: ExamHistory,
): boolean {
  // An empty filter means "no restriction", not "match nothing" — otherwise the default criteria
  // would select an empty exam.
  const { classification } = question;

  if (criteria.range !== null && (position < criteria.range.from || position > criteria.range.to)) {
    return false;
  }
  if (
    criteria.incorrectAtLeast !== null &&
    incorrectCount(history, question.id) < criteria.incorrectAtLeast
  ) {
    return false;
  }
  if (criteria.types.length > 0 && !criteria.types.includes(question.type)) {
    return false;
  }
  if (
    criteria.difficulties.length > 0 &&
    (classification.difficulty === undefined ||
      !criteria.difficulties.includes(classification.difficulty))
  ) {
    return false;
  }
  if (
    criteria.categoryIds.length > 0 &&
    (classification.categoryId === undefined ||
      !criteria.categoryIds.includes(classification.categoryId))
  ) {
    return false;
  }
  if (
    criteria.topicIds.length > 0 &&
    !classification.topicIds.some((topicId) => criteria.topicIds.includes(topicId))
  ) {
    return false;
  }
  if (criteria.tags.length > 0 && !classification.tags.some((tag) => criteria.tags.includes(tag))) {
    return false;
  }

  return true;
}

/**
 * The order this question's options should be shown in, or null to show them as authored.
 *
 * A question may opt out of shuffling (`shuffleOptions: false`) when its options have an inherent
 * order — numeric ranges, for instance, read as nonsense out of sequence.
 */
function shuffledOptionIds(
  question: CanonicalQuestion,
  policy: SessionPolicy,
  key: string,
): readonly OptionId[] | null {
  const prng = prngFrom(`${key}|${question.id}`);

  switch (question.type) {
    case 'single-choice':
    case 'multiple-choice': {
      const enabled = question.interaction.shuffleOptions ?? policy.shuffleOptions;
      return enabled
        ? shuffleAnchored(question.interaction.options, prng).map((option) => option.id)
        : null;
    }

    case 'matching': {
      // The items keep their authored order; the targets are the answer surface, so those are what
      // shuffling has to move.
      const enabled = question.interaction.shuffleTargets ?? policy.shuffleOptions;
      return enabled
        ? shuffleAnchored(question.interaction.targets, prng).map((target) => target.id)
        : null;
    }

    case 'ordering':
      // Presenting an ordering question in the correct order would give the answer away, so its
      // items are shuffled whenever the session shuffles anything at all.
      return policy.shuffleOptions
        ? shuffleAnchored(question.interaction.items, prng).map((item) => item.id)
        : null;

    case 'text':
      return null;
  }
}

/**
 * Everything that must change **which questions are drawn**, and nothing that must not.
 *
 * The exam is identified by its content hash rather than its id, so two files claiming the same id
 * cannot silently share a draw. The criteria are serialised in a fixed field order, with arrays
 * sorted, so that a UI listing filters in a different order still produces the same key.
 */
export function selectionKey(exam: CanonicalExam, criteria: SelectionCriteria, seed: Seed): string {
  const sortedList = (values: readonly string[]): string => [...values].sort().join(',');

  return [
    exam.contentHash,
    String(criteria.count),
    criteria.range === null ? '' : `${String(criteria.range.from)}-${String(criteria.range.to)}`,
    String(criteria.incorrectAtLeast ?? ''),
    sortedList(criteria.categoryIds),
    sortedList(criteria.topicIds),
    sortedList(criteria.tags),
    sortedList(criteria.difficulties),
    sortedList(criteria.types),
    seed,
  ].join('|');
}

/**
 * The key for option order: the exam and the seed, and nothing else.
 *
 * Leaving the criteria out is the whole point. If option order depended on the filters, narrowing a
 * category or dropping the question count from 65 to 20 would reshuffle the options of every
 * question that survived — the same question would look different for no reason the user can see.
 */
export function optionKey(exam: CanonicalExam, seed: Seed): string {
  return `${exam.contentHash}|${seed}`;
}

/** A short, readable seed the user can write down, share, or type back in. */
export function randomSeed(random: () => number = Math.random): Seed {
  const alphabet = '23456789abcdefghjkmnpqrstuvwxyz';
  let value = '';
  for (let index = 0; index < 8; index += 1) {
    value += alphabet.charAt(Math.floor(random() * alphabet.length));
  }
  return brandAs<Seed>(value);
}
